import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Metric } from "../components/Metric";
import { formatDate, formatMoney, formatMoneyAxis } from "../lib/format";
import {
  fetchDashboard,
  registryHref,
  type AgingBucket,
  type Dashboard,
  type StatusSlice,
  type TopCounterpartyRow,
  type TrendPoint,
} from "../lib/dashboard";
import { obligationHref, type SummaryRow } from "../lib/summary";

/**
 * Дашборд-визуализация (E12a) — те же данные, что в сводке, но картинками: динамика просрочек,
 * возрастная структура долгов, топ контрагентов по санкциям, распределение по статусам и
 * эскалации. Право — `summary:view` (как у сводки); делопроизводителю отказывает сервер.
 *
 * ТРИ ПРАВИЛА, ПО КОТОРЫМ СДЕЛАН ЭКРАН.
 *
 * 1. НИ ОДНА ЦИФРА НЕ ЖИВЁТ ТОЛЬКО В КАРТИНКЕ. Под каждым графиком стоят те же числа строками —
 *    и это не дублирование ради красоты: график недоступен для чтения с экрана, не переживает
 *    чёрно-белую печать и не даёт нажать на сегмент с клавиатуры. Строки закрывают всё это разом
 *    и заодно служат переходом в реестр (docs/07 §2 «смысл не передаётся одним цветом»).
 * 2. ЦВЕТ — ТОЛЬКО ТОКЕНЫ docs/07 (правило 9): в конфигурации диаграмм нет ни одного «сырого»
 *    hex, что закреплено тестом-сторожем. Шкала возраста строится смешением ОДНОГО статусного
 *    токена с фоном — так она остаётся одноцветной и темнеет монотонно.
 * 3. ФОРМА ПО ЗАДАЧЕ: изменение во времени — линии, сравнение величин — полосы, одно число —
 *    плитка. Круговых диаграмм нет: доли на глаз не сравниваются, а у нас семь статусов.
 */

/** Высота области графика. Одна на все блоки — иначе колонки «пляшут» по вертикали. */
const CHART_HEIGHT = 240;

/**
 * Цвета линий динамики: наши требования — акцентный, наши собственные просрочки — красный «риска»
 * (тем же тоном сводка красит экспозицию). Пара проверена расчётом на различимость при
 * дальтонизме: ΔE 19,9 в протанопии при пороге 8 — то есть линии различает и тот, кто не видит
 * разницы между красным и зелёным. Плюс у линий разное начертание (см. `strokeDasharray`).
 */
const TREND_THEY = "var(--color-accent)";
const TREND_WE = "var(--color-overdue-fg)";

/**
 * Шкала возрастной структуры — ОДИН тон, четыре ступени: чем дольше просрочка, тем плотнее цвет.
 * Смешение с фоном (`color-mix`) вместо четырёх новых цветов: новых токенов эпик не заводит,
 * а последовательная шкала обязана быть одноцветной (радуга в ней читается как разные сущности).
 */
const AGING_SCALE = [
  "color-mix(in srgb, var(--color-overdue-fg) 30%, var(--color-surface))",
  "color-mix(in srgb, var(--color-overdue-fg) 52%, var(--color-surface))",
  "color-mix(in srgb, var(--color-overdue-fg) 75%, var(--color-surface))",
  "var(--color-overdue-fg)",
];

/**
 * Статусы носят свои собственные цвета (Прил. Б, docs/07): они зарезервированы за состоянием.
 *
 * «Снято» в палитре делит серый тон с «Ожидает события» — на штампах это незаметно, потому что
 * рядом стоит текст, а на диаграмме два сегмента одного цвета читались бы как один. Поэтому
 * снятое берётся тем же токеном, но светлее: тон остаётся серым, различие появляется.
 */
const STATUS_COLOR: Record<string, string> = {
  awaiting: "var(--color-waiting-fg)",
  control: "var(--color-control-fg)",
  soon: "var(--color-soon-fg)",
  overdue: "var(--color-overdue-fg)",
  fulfilled: "var(--color-done-fg)",
  dispute: "var(--color-dispute-fg)",
  cancelled: "color-mix(in srgb, var(--color-waiting-fg) 45%, var(--color-surface))",
};

/** Оформление осей и подсказок — общее, чтобы графики читались как один набор. */
const AXIS = { fontFamily: "var(--font-num)", fontSize: 12, fill: "var(--color-muted)" } as const;
const GRID_COLOR = "var(--color-border)";
const TOOLTIP_STYLE = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-m)",
  fontFamily: "var(--font-ui)",
  fontSize: 13,
} as const;

const linkCls = "text-accent hover:underline";

/** `2026-08-06` → `06.08` — подпись оси; год в ряду из 26 недель только мешает. */
function shortDate(iso: string): string {
  return formatDate(iso).slice(0, 5);
}

/**
 * Деньги строкой «37000.00» → целые копейки для ДЛИНЫ столбика. Разбор строковый, без дробных
 * чисел: копейки — целое, и длина столбика от него не страдает. Мусор читается как ноль — на
 * геометрию это влияет как «нечего рисовать», а суммы на экран идут не отсюда.
 */
function kopecksOf(money: string): number {
  const [whole = "0", frac = ""] = money.split(".");
  return (Number(whole) || 0) * 100 + (Number((frac + "00").slice(0, 2)) || 0);
}

export function DashboardPage() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await fetchDashboard();
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setData(res.value);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) {
    return (
      <Shell>
        <p role="alert" className="mt-3 text-[13px] text-overdue-fg">
          {error}
        </p>
        <button
          type="button"
          className="mt-3 rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg"
          onClick={() => void load()}
        >
          Повторить
        </button>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell>
        <p role="status" className="mt-3 text-[13px] text-muted">
          Загрузка…
        </p>
      </Shell>
    );
  }

  // Данных нет вовсе — рисовать пустые оси незачем, это выглядит поломкой. Говорим, что делать.
  if (!data.hasData) {
    return (
      <Shell>
        <p className="mt-4 text-[14px]">
          Показывать пока нечего: обязательств по действующим договорам в системе нет. Заведите
          договор с обязательствами — и здесь появится картина по срокам, долгам и просрочкам.
          Договоры, переведённые в архив, в этот раздел не попадают.
        </p>
        <p className="mt-2 text-[14px]">
          <Link className={linkCls} to="/contracts">
            Перейти к договорам
          </Link>
        </p>
      </Shell>
    );
  }

  const overdueTotal = data.statuses.find((s) => s.status === "overdue")?.count ?? 0;

  return (
    <Shell>
      <p className="mt-1 text-[13px] text-muted">Состояние на {formatDate(data.today)}.</p>

      {/* Просрочек нет — это ХОРОШЕЕ состояние, и о нём надо сказать словами, а не оставлять
          человека гадать, почему блоки пустые. */}
      {overdueTotal === 0 && (
        <p
          role="status"
          className="mt-3 rounded-[var(--radius-m)] border border-border bg-done-bg px-3 py-2 text-[14px] font-bold text-done-fg"
        >
          Просрочек нет — всё в порядке.
        </p>
      )}

      <div className="mt-4 grid gap-4 xl:grid-cols-2 2xl:grid-cols-3">
        <TrendBlock weeks={data.trend.weeks} />
        <AgingBlock aging={data.aging} />
        <TopBlock top={data.top} />
        <StatusBlock statuses={data.statuses} />
      </div>

      <EscalationsBlock escalations={data.escalations} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section aria-labelledby="dashboard-title">
      <h1 id="dashboard-title" className="text-[20px] font-bold text-ink">
        Дашборд
      </h1>
      {children}
    </section>
  );
}

/** Карточка блока: заголовок, пояснение простым языком и содержимое. */
function Block({
  title,
  hint,
  children,
  span,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
  /**
   * Занять всю ширину сетки — только там, где колонок три (2xl, от 1536 px).
   *
   * На ширине ноутбука колонок две, и растянутая на обе динамика оставила бы «Статусы» одни в
   * половине ряда — дырку, которой до пакета 2 не было. Три равных блока под широким графиком
   * получаются лишь при трёх колонках.
   */
  span?: boolean;
}) {
  return (
    <div
      className={`rounded-[var(--radius-l)] border border-border bg-surface p-4 ${
        span ? "2xl:col-span-3" : ""
      }`}
    >
      <h2 className="text-[15px] font-bold text-ink">{title}</h2>
      <p className="mt-1 text-[12px] text-muted">{hint}</p>
      {children}
    </div>
  );
}

/** Динамика просрочек по неделям: две линии — требования к контрагентам и наши собственные. */
function TrendBlock({ weeks }: { weeks: TrendPoint[] }) {
  const last = weeks.at(-1);
  return (
    <Block
      span
      title="Динамика просрочек по неделям"
      hint="Сколько обязательств было просрочено на конец каждой недели за полгода. Прошлое пересчитывается по датам документов, поэтому внесённый задним числом платёж меняет и картину прошлых недель."
    >
      <div style={{ height: CHART_HEIGHT }} className="mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={weeks} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={AXIS} interval={4} />
            <YAxis allowDecimals={false} tick={AXIS} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              labelFormatter={(label) => (typeof label === "string" ? formatDate(label) : "")}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="they"
              name="Просрочки контрагентов"
              stroke={TREND_THEY}
              strokeWidth={2}
              dot={false}
            />
            {/* Вторая линия отличается не только цветом, но и штрихом: на чёрно-белой печати и
                при нарушении цветовосприятия иначе не различить (docs/07 §2). */}
            <Line
              type="monotone"
              dataKey="we"
              name="Наши просрочки"
              stroke={TREND_WE}
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {last !== undefined && (
        <p className="mt-2 text-[13px]">
          Сейчас:{" "}
          <Link className={linkCls} to={registryHref.overdueOfCounterparties()}>
            просрочки контрагентов — {last.they}
          </Link>
          {", "}
          <Link className={linkCls} to={registryHref.overdueOfOurs()}>
            наши просрочки — {last.we}
          </Link>
        </p>
      )}
    </Block>
  );
}

/** Возрастная структура дебиторки: 0–30 / 31–60 / 61–90 / более 90 дней. */
function AgingBlock({ aging }: { aging: Dashboard["aging"] }) {
  const navigate = useNavigate();
  const href = registryHref.overdueOfCounterparties();

  return (
    <Block
      title="Возрастная структура долгов контрагентов"
      hint="Остаток долга по просроченным обязательствам контрагентов, разложенный по сроку просрочки. Чем правее сегмент, тем дольше не платят."
    >
      {aging.count === 0 ? (
        <p className="mt-3 text-[14px] text-done-fg">Просроченных долгов контрагентов нет.</p>
      ) : (
        <>
          <div style={{ height: CHART_HEIGHT }} className="mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={aging.buckets}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 34 }}
                onClick={() => navigate(href)}
              >
                <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={AXIS} allowDecimals={false} />
                <YAxis type="category" dataKey="label" tick={AXIS} width={92} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value) => [String(value), "обязательств"]}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {aging.buckets.map((b, i) => (
                    <Cell key={b.id} fill={AGING_SCALE[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 text-[13px]">
            {aging.buckets.map((b: AgingBucket) => (
              <li
                key={b.id}
                className="flex justify-between gap-3 border-b border-border-soft py-1"
              >
                <Link className={linkCls} to={href}>
                  {b.label}
                </Link>
                <span className="num text-muted">
                  {b.count} · {formatMoney(b.debt)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[12px] text-muted">
            Всего просрочено {aging.count} на {formatMoney(aging.debt)}. Переход открывает все
            просрочки контрагентов: отбора по числу дней в реестре пока нет.
          </p>
        </>
      )}
    </Block>
  );
}

/** Топ-5 контрагентов по начисленным санкциям. */
function TopBlock({ top }: { top: Dashboard["top"] }) {
  const navigate = useNavigate();

  return (
    <Block
      title="Топ-5 контрагентов по санкциям"
      hint="Сколько начислено по просрочкам каждого контрагента. Сюда входит и то, что уже погашено с опозданием: санкции по нему остались."
    >
      {top.rows.length === 0 ? (
        <p className="mt-3 text-[14px] text-done-fg">Начисленных санкций нет.</p>
      ) : (
        <>
          <div style={{ height: CHART_HEIGHT }} className="mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                // Длина столбика — в целых КОПЕЙКАХ, а не в рублях с дробной частью: число здесь
                // задаёт только геометрию, и брать для него дробное значение незачем (правило 2 —
                // деньги через число с плавающей точкой не ходят). Все ПОКАЗЫВАЕМЫЕ суммы
                // форматируются из исходной строки — и в списке под графиком, и в подсказке.
                data={top.rows.map((r) => ({ ...r, value: kopecksOf(r.sanctions) }))}
                layout="vertical"
                margin={{ top: 8, right: 12, bottom: 0, left: 34 }}
                onClick={(state) => {
                  const row = top.rows[Number(state.activeIndex)];
                  if (row) navigate(registryHref.counterparty(row.counterpartyId));
                }}
              >
                <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" horizontal={false} />
                {/* Ось получает КОПЕЙКИ (ими задана длина столбика) и обязана печатать рубли:
                    без форматтера на делениях стояли бы «15000000» — дефект приёмки 04.09.2026. */}
                <XAxis
                  type="number"
                  tick={AXIS}
                  tickFormatter={(value: number) => formatMoneyAxis(value)}
                />
                <YAxis type="category" dataKey="counterpartyName" tick={AXIS} width={120} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  // Сумма берётся из ИСХОДНОЙ строки-денег в данных точки, а не из числа,
                  // которым нарисован столбик: показывать деньги, восстановленные из float,
                  // нельзя даже во всплывающей подсказке.
                  formatter={(_value, _name, item) => {
                    const row = item?.payload as TopCounterpartyRow | undefined;
                    return [formatMoney(row?.sanctions), "начислено"];
                  }}
                />
                {/* Одна серия — один цвет: раскрашивать столбцы разными тонами значило бы
                    намекнуть на несуществующие категории. */}
                <Bar dataKey="value" fill="var(--color-accent)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 text-[13px]">
            {top.rows.map((r) => (
              <li
                key={r.counterpartyId}
                className="flex justify-between gap-3 border-b border-border-soft py-1"
              >
                <Link className={linkCls} to={registryHref.counterparty(r.counterpartyId)}>
                  {r.counterpartyName}
                </Link>
                <span className="num text-muted">{formatMoney(r.sanctions)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[12px] text-muted">
            {top.others.count > 0
              ? `Остальные контрагенты (${top.others.count}): ${formatMoney(top.others.sanctions)}. `
              : ""}
            Всего начислено {formatMoney(top.total)}.
          </p>
        </>
      )}
    </Block>
  );
}

/** Распределение обязательств по статусам Приложения Б. */
function StatusBlock({ statuses }: { statuses: StatusSlice[] }) {
  const navigate = useNavigate();
  const shown = statuses.filter((s) => s.count > 0);

  return (
    <Block
      title="Обязательства по статусам"
      hint="Все обязательства действующих договоров. Нажатие открывает реестр ровно с этим отбором."
    >
      <div style={{ height: CHART_HEIGHT }} className="mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={shown}
            layout="vertical"
            margin={{ top: 8, right: 12, bottom: 0, left: 34 }}
            onClick={(state) => {
              const row = shown[Number(state.activeIndex)];
              if (row) navigate(registryHref.status(row.status));
            }}
          >
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={AXIS} allowDecimals={false} />
            <YAxis type="category" dataKey="label" tick={AXIS} width={120} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value) => [String(value), "обязательств"]}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {shown.map((s) => (
                <Cell key={s.status} fill={STATUS_COLOR[s.status]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-2 text-[13px]">
        {statuses.map((s) => (
          <li
            key={s.status}
            className="flex justify-between gap-3 border-b border-border-soft py-1"
          >
            <Link className={linkCls} to={registryHref.status(s.status)}>
              {s.label}
            </Link>
            <span className="num text-muted">{s.count}</span>
          </li>
        ))}
      </ul>
    </Block>
  );
}

/**
 * Флаги эскалаций — одно число и короткий список. Графика здесь нет намеренно: величина одна,
 * и столбик из одного значения сообщает меньше, чем сама цифра.
 */
function EscalationsBlock({ escalations }: { escalations: Dashboard["escalations"] }) {
  return (
    <div className="mt-4 rounded-[var(--radius-l)] border border-border bg-surface p-4">
      <h2 className="text-[15px] font-bold text-ink">Флаги эскалаций</h2>
      <p className="mt-1 text-[12px] text-muted">
        Просрочки дольше порога, заданного в настройках системы. По ним ушёл сигнал директору и
        юристу.
      </p>

      {escalations.total === 0 ? (
        <p className="mt-3 text-[14px] text-done-fg">Эскалаций нет.</p>
      ) : (
        <>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <Metric
              title="Всего эскалаций"
              value={escalations.total}
              caption="просрочек сверх порога"
              risk
            />
            <Metric
              title="У контрагентов"
              value={escalations.they}
              caption="их просрочки перед нами"
            />
            <Metric title="У нас" value={escalations.we} caption="наши просрочки" risk />
          </div>

          <ul className="mt-3 text-[13px]">
            {escalations.rows.map((row: SummaryRow) => (
              <li key={row.obligationId} className="border-b border-border-soft py-1.5">
                <Link className={linkCls} to={obligationHref(row)}>
                  {row.contractNumber}
                </Link>{" "}
                — {row.counterpartyName}, {row.descr}.{" "}
                <span className="num text-overdue-fg">просрочка {row.overdue} дн.</span>
              </li>
            ))}
          </ul>

          <p className="mt-2 text-[13px]">
            <Link className={linkCls} to={registryHref.allOverdue()}>
              Все просрочки в реестре обязательств
            </Link>
          </p>
        </>
      )}
    </div>
  );
}
